import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X, Crosshair, ExternalLink } from 'lucide-react';
import api from '@/utils/api';
import MarketStatusBanner from './MarketStatusBanner';

const KIND_ICONS = {
  opportunity_a_plus: <Crosshair className="w-3.5 h-3.5 text-orange-400" />,
  default: <Bell className="w-3.5 h-3.5 text-cyan-400" />,
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'الآن';
  if (m < 60) return `قبل ${m} د`;
  const h = Math.floor(m / 60);
  if (h < 24) return `قبل ${h} س`;
  const d = Math.floor(h / 24);
  return `قبل ${d} يوم`;
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);

  // Poll every 30 seconds
  const fetchData = async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      const allItems = res.data.items || [];
      setItems(allItems);
      // Count unread ACTIVE items only (exclude expired/stale opportunities)
      const activeUnread = allItems.filter(
        (n) => !n.read && !n.price_expired && !n.is_stale
      ).length;
      setUnread(activeUnread);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, []);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read');
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // silent
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-lg hover:bg-white/5 transition-colors"
        data-testid="notifications-bell-btn"
        aria-label="الإشعارات"
      >
        <Bell className="w-4 h-4 text-zinc-300" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 sm:left-auto sm:right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] bg-zinc-950 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notifications-dropdown">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-bold text-white">الإشعارات</h3>
              {unread > 0 && <span className="text-[9px] text-zinc-400">({unread} غير مقروءة)</span>}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[10px] text-cyan-400 hover:text-cyan-300 px-2"
                  data-testid="mark-all-read-btn"
                >
                  تحديد الكل كمقروء
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/5 rounded">
                <X className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </div>
          </div>

          <div className="p-2">
            <MarketStatusBanner compact />
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-xs">لا توجد إشعارات بعد</p>
                <p className="text-zinc-600 text-[10px] mt-1">عند ظهور فرصة A+ ستصلك هنا</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {items.map((n) => (
                  <NotificationItem key={n.id} n={n} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationItem({ n }) {
  const icon = KIND_ICONS[n.kind] || KIND_ICONS.default;
  const meta = n.meta || {};
  const isOpp = n.kind === 'opportunity_a_plus';
  const grade = meta.grade || '';
  const direction = meta.direction === 'BUY' ? 'شراء' : meta.direction === 'SELL' ? 'بيع' : '';
  const dirColor = meta.direction === 'BUY' ? 'text-emerald-400' : 'text-red-400';
  const isExpired = !!n.price_expired;
  const isStale = !!n.is_stale && !isExpired;

  return (
    <div className={`p-3 hover:bg-white/[0.02] ${!n.read ? 'bg-cyan-500/[0.03]' : ''} ${isExpired ? 'opacity-50' : ''}`} data-testid="notif-item">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className={`text-xs font-bold ${!n.read ? 'text-white' : 'text-zinc-300'}`}>{n.title}</h4>
            {!n.read && !isExpired && <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-1 flex-shrink-0" />}
          </div>
          {isOpp && (isExpired || isStale) && (
            <div className={`mb-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${
              isExpired
                ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
            }`}>
              {isExpired ? '⛔ منتهية — السعر تجاوز نقطة الدخول' : '⚠️ قد تكون قديمة (راجع الصياد للتأكيد)'}
            </div>
          )}
          {isOpp ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-[10px]">
                <span className={`font-bold ${dirColor}`}>{direction}</span>
                {grade && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[9px] font-bold">{grade}</span>}
                <span className="text-zinc-500">Score: {meta.score}/100</span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-[10px] text-zinc-400">
                <span>دخول: <span className="text-white font-mono">{meta.entry}</span></span>
                <span>SL: <span className="text-red-400 font-mono">{meta.sl}</span></span>
                <span>TP1: <span className="text-emerald-400 font-mono">{meta.tp1}</span></span>
                <span>TP5: <span className="text-emerald-300 font-mono">{meta.tp5 || meta.tp3}</span></span>
              </div>
              {n.current_price && (
                <div className="text-[9.5px] text-zinc-500">
                  السعر الآن: <span className="font-mono text-zinc-300">{n.current_price}</span>
                </div>
              )}
              {!isExpired && (
                <Link
                  to="/ai-deals"
                  state={{ pinnedOpportunity: { ...meta, notification_id: n.id, notification_age_min: n.age_minutes, current_price: n.current_price } }}
                  className="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 mt-1"
                >
                  <ExternalLink className="w-3 h-3" /> اعرض هذه الصفقة
                </Link>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-zinc-400">{n.body}</p>
          )}
          <p className="text-[9px] text-zinc-600 mt-1">{timeAgo(n.created_at)}</p>
        </div>
      </div>
    </div>
  );
}
